import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getKpiInsight, STATUS_COLOR } from './utils/kpiInsight';

// ── SVG Gradient Gauge ────────────────────────────────────────────────────────
// Semi-circle: left (9 o'clock) = 0, top (12 o'clock) = 1.0, right (3 o'clock) = 2.0
const CX = 100, CY = 105, R = 75, SW = 8;
// sweep-flag=1 (clockwise in SVG screen space) → left → top → right = top arc ✓
const ARC_PATH = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

const SvgGauge = ({ percent, id }) => {
    const clamped = Math.min(Math.max(percent, 0), 1);
    const angle = Math.PI * (1 - clamped); // π (left) → 0 (right)
    const nLen = 58;
    const nx = CX + nLen * Math.cos(angle);
    const ny = CY - nLen * Math.sin(angle); // SVG y is flipped

    return (
        <svg viewBox="0 0 200 115" style={{ width: 70, display: 'block' }}>
            <defs>
                {/* gradient runs along x-axis matching the arc's left/right endpoints */}
                <linearGradient
                    id={`gg-${id}`}
                    x1={CX - R} y1="0" x2={CX + R} y2="0"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0%"   stopColor="#EF4444" />
                    <stop offset="44%"  stopColor="#F59E0B" />
                    <stop offset="56%"  stopColor="#22C55E" />
                    <stop offset="100%" stopColor="#22C55E" />
                </linearGradient>
            </defs>
            {/* Track */}
            <path d={ARC_PATH} fill="none" stroke="#e5e7eb" strokeWidth={SW} strokeLinecap="round" />
            {/* Gradient arc (always full — shows the scale) */}
            <path d={ARC_PATH} fill="none" stroke={`url(#gg-${id})`} strokeWidth={SW} strokeLinecap="round" />
            {/* Needle */}
            <line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#374151" strokeWidth={1.5} strokeLinecap="round" />
            {/* Needle base */}
            <circle cx={CX} cy={CY} r={3.5} fill="#374151" />
        </svg>
    );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const formatVal = (v, type, currencySymbol) => {
    if (v === null || v === undefined || isNaN(Number(v))) return '—';
    const s = Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return type === 'currency' ? `${currencySymbol}${s}` : s;
};

const TooltipPanel = ({ insight, value, data, settings, x, y }) => {
    if (!insight) return null;
    const statusColor = STATUS_COLOR[insight.status ?? 'red'];
    return (
        <div className="kpi-gauge-tooltip" style={{ left: x + 14, top: y + 14 }}>
            <div className="kpi-tt-header">
                <span className="kpi-tt-dot" style={{ background: statusColor }} />
                <strong>{insight.title}</strong>
            </div>
            <div className="kpi-tt-divider" />
            <div className="kpi-tt-row">
                <span className="kpi-tt-key">Value</span>
                <span className="kpi-tt-val">{Number(value ?? 0).toFixed(2)}</span>
            </div>
            <div className="kpi-tt-row">
                <span className="kpi-tt-key">Formula</span>
                <span className="kpi-tt-val kpi-tt-mono">{insight.formula}</span>
            </div>
            <div className="kpi-tt-divider" />
            <div className="kpi-tt-insight">{insight.insight}</div>
            <div className="kpi-tt-divider" />
            {insight.components.map((c) => (
                <div key={c.key} className="kpi-tt-component">
                    <span className="kpi-tt-comp-label">{c.label}</span>
                    <span className="kpi-tt-comp-value">{formatVal(c.value, c.type, settings.currencySymbol)}</span>
                    <span className="kpi-tt-comp-note">← {c.note}</span>
                </div>
            ))}
        </div>
    );
};

// ── Section ───────────────────────────────────────────────────────────────────
const GAUGES = ['cpi', 'ler'];

const KpiGaugeSection = ({
    kpis,
    view,
    onViewChange,
    isSyncing,
    settings = { currencySymbol: '$', groupSeparator: ',', decimalSeparator: '.' },
}) => {
    const data = view === 'ytd' ? kpis?.ytd : kpis?.monthly;

    // Single tooltip state lifted here — prevents two tooltips showing at once
    const [active, setActive] = useState(null); // { kpi, x, y }

    const handleSectionMouseMove = (e) => {
        // Only update position when a gauge is already active (avoids spurious renders)
        setActive(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
    };

    const handleEnter = (kpi) => (e) => setActive({ kpi, x: e.clientX, y: e.clientY });
    const handleLeave = () => setActive(null);

    const activeInsight = active ? getKpiInsight(active.kpi, data?.[active.kpi] ?? 0, data) : null;

    return (
        <div className="kpi-gauge-section" onMouseMove={handleSectionMouseMove}>
            {/* Toggle */}
            <div className="kpi-toggle">
                <button className={view === 'ytd' ? 'active' : ''} onClick={() => onViewChange('ytd')}>YTD</button>
                <button className={view === 'monthly' ? 'active' : ''} onClick={() => onViewChange('monthly')}>Current Month</button>
            </div>

            {/* Legend */}
            <div className="kpi-legend">
                <span className="kpi-legend-item kpi-legend-item--planned">Planned</span>
                <span className="kpi-legend-item kpi-legend-item--actuals">Actuals</span>
            </div>

            {/* Visual separator */}
            <span className="kpi-gauge-separator" />

            {/* Gauges */}
            <div className="kpi-gauges-row">
                {GAUGES.map((kpi) => {
                    const value = data?.[kpi];
                    const percent = Math.min(Math.max((value ?? 0) / 2, 0), 1);
                    const insight = getKpiInsight(kpi, value ?? 0, data);
                    const statusColor = STATUS_COLOR[insight?.status ?? 'red'];
                    return (
                        <div
                            key={kpi}
                            className={`kpi-gauge-card kpi-gauge-card--${insight?.status}`}
                            onMouseEnter={handleEnter(kpi)}
                            onMouseLeave={handleLeave}
                        >
                            <SvgGauge percent={percent} id={kpi} />
                            <div className="kpi-gauge-footer">
                                <span className="kpi-gauge-label">{insight?.label}</span>
                                <span className="kpi-gauge-value" style={{ color: statusColor }}>
                                    {value !== null && value !== undefined ? Number(value).toFixed(2) : '—'}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isSyncing && (
                <span className="kpi-sync-indicator">
                    <Loader2 size={12} className="kpi-spinner" />
                    Syncing…
                </span>
            )}
            {/* Single tooltip rendered at section level */}
            {active && (
                <TooltipPanel
                    insight={activeInsight}
                    value={data?.[active.kpi]}
                    data={data}
                    settings={settings}
                    x={active.x}
                    y={active.y}
                />
            )}
        </div>
    );
};

export default KpiGaugeSection;
